import { useState, useMemo, useEffect, useRef } from 'react'
import dayjs from 'dayjs'
import { Box, ToggleButton, ToggleButtonGroup, Typography, CircularProgress, Alert } from '@mui/material'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import ViewDayIcon from '@mui/icons-material/ViewDay'
import AssessmentIcon from '@mui/icons-material/Assessment'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { useOverallRosterData } from './hooks/useOverallRosterData'
import { useAllowedClinics } from './hooks/useAllowedClinics'
import WeeklyView from './components/WeeklyView'
import DailyView from './components/DailyView'
import SummaryView from './components/SummaryView'
import RosterFilters from './components/RosterFilters'
import { OverallRosterContainer, ViewModeContainer } from './components/styledComponents'
import RosterForm from 'src/supportPortal/views/roster/component/RosterForm'
import ClinicRosterItemViewer from 'src/supportPortal/views/roster/component/viewers/ClinicRosterItemViewer'
import { Dialog } from '@mui/material'
import { usePopoverState } from 'src/customHooks/usePopoverState'
import RosterPopover from 'src/supportPortal/components/common/roster/RosterPopover'
import './rosterPrintStyles.css'


export default function OverallRosterPage() {
  const allRoles = getAllRolesInBoolean()
  const isClinicRosterAdmin = allRoles.isClinicRosterAdmin
  const isClinicRosterManager = allRoles.isClinicRosterManager

  // Get allowed clinics for manager
  const { allowedClinicIds, allowedClinics, loading: loadingClinics } = useAllowedClinics(isClinicRosterManager)

  // View mode state
  const [viewMode, setViewMode] = useState('weekly') // 'weekly' | 'daily' | 'summary'

  // Date state
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [selectedWeek, setSelectedWeek] = useState(dayjs().startOf('week'))

  // Filter state
  const [selectedRoomIds, setSelectedRoomIds] = useState([])
  const [selectedTargetSpecialitySlugs, setSelectedTargetSpecialitySlugs] = useState([])
  const [selectedEventTypes, setSelectedEventTypes] = useState([])

  // Popover state for slot creation/editing
  const popoverState = usePopoverState()

  // Calculate date range based on view mode
  const { fromDate, toDate } = useMemo(() => {
    if (viewMode === 'weekly') {
      return {
        fromDate: selectedWeek.startOf('week'),
        toDate: selectedWeek.endOf('week'),
      }
    } else if (viewMode === 'daily') {
      return {
        fromDate: selectedDate,
        toDate: selectedDate,
      }
    } else {
      // Summary view - default to current week
      return {
        fromDate: dayjs().startOf('week'),
        toDate: dayjs().endOf('week'),
      }
    }
  }, [viewMode, selectedWeek, selectedDate])

  // Determine clinic filter based on role and selected rooms
  // Extract clinic brand IDs from selected rooms
  const effectiveClinicIds = useMemo(() => {
    if (isClinicRosterAdmin) {
      // Admin can see all clinics
      return null
    } else if (isClinicRosterManager) {
      // Manager limited to allowed clinics
      return allowedClinicIds
    }
    return []
  }, [isClinicRosterAdmin, isClinicRosterManager, allowedClinicIds])

  // Fetch roster data
  const { rosterData, summary, unoccupiedSlots, loading, error, refetch } = useOverallRosterData({
    fromDate,
    toDate,
    clinicBrandIds: effectiveClinicIds,
    chamberRoomIds: selectedRoomIds.length > 0 ? selectedRoomIds : null,
    targetSpecialitySlugs: selectedTargetSpecialitySlugs.length > 0 ? selectedTargetSpecialitySlugs : null,
    eventTypes: selectedEventTypes.length > 0 ? selectedEventTypes : null,
    includeUnoccupied: true,
  })

  // Group roster data by room and date for easier rendering
  const groupedRosterData = useMemo(() => {
    const grouped = new Map()

    rosterData.forEach((slot) => {
      const key = `${slot.chamberRoomId}_${slot.customDate}`
      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      grouped.get(key).push(slot)
    })

    return grouped
  }, [rosterData])

  // Store full chamber room data (from RosterFilters) for accessing mentorsAndAuditors
  const [chamberRoomsData, setChamberRoomsData] = useState(new Map())
  // Store target specialities for displaying in room titles
  const [targetSpecialities, setTargetSpecialities] = useState([])
  
  // Handle room data update from RosterFilters
  const handleRoomsDataChange = (rooms) => {
    const roomMap = new Map()
    rooms.forEach((room) => {
      if (room.id) {
        roomMap.set(room.id, room)
      }
    })
    setChamberRoomsData(roomMap)
  }

  // Handle target specialities update from RosterFilters
  const handleTargetSpecialitiesChange = (specialities) => {
    setTargetSpecialities(specialities || [])
  }

  // Helper function to extract clinic brand name from room data (handles nested objects)
  const getClinicBrandName = (roomData) => {
    if (!roomData) return null
    return (
      roomData.clinicBrand?.name ||
      roomData.clinicBrand?.title ||
      roomData.clinicBrandName ||
      null
    )
  }

  // Helper function to extract clinic location name from room data (handles nested objects)
  const getClinicLocationName = (roomData) => {
    if (!roomData) return null
    return (
      roomData.clinicLocation?.name ||
      roomData.clinicLocation?.title ||
      roomData.clinicLocationName ||
      null
    )
  }

  // Extract unique rooms with details, filtered by chamberRoomsData (which is already filtered by Target Speciality and Event Type)
  const rooms = useMemo(() => {
    // Get list of valid room IDs from chamberRoomsData (these are already filtered by the dropdown filters)
    const validRoomIds = new Set(Array.from(chamberRoomsData.keys()))
    
    // If chamberRoomsData is empty (no rooms fetched yet or no filters), extract from rosterData
    // Otherwise, only show rooms that are in the filtered chamberRoomsData
    if (validRoomIds.size === 0) {
      // No filters applied or rooms not loaded yet - show all rooms from rosterData
      const roomMap = new Map()
      rosterData.forEach((slot) => {
        if (slot.chamberRoomId && !roomMap.has(slot.chamberRoomId)) {
          roomMap.set(slot.chamberRoomId, {
            id: slot.chamberRoomId,
            title: slot.chamberRoomTitle || `Room ${slot.chamberRoomId}`,
            clinicLocationName: slot.clinicLocationName,
            clinicBrandName: slot.clinicBrandName,
          })
        }
      })
      unoccupiedSlots.forEach((slot) => {
        if (slot.chamberRoomId && !roomMap.has(slot.chamberRoomId)) {
          roomMap.set(slot.chamberRoomId, {
            id: slot.chamberRoomId,
            title: slot.chamberRoomTitle || `Room ${slot.chamberRoomId}`,
            clinicLocationName: slot.clinicLocationName,
            clinicBrandName: slot.clinicBrandName,
          })
        }
      })
      return Array.from(roomMap.values())
    }
    
    // Filters are applied - prioritize chamberRoomsData as source of truth for all room information
    // This ensures consistent clinic brand and location names for all rooms
    const roomMap = new Map()
    
    // First, add all rooms from chamberRoomsData (this is the authoritative source)
    chamberRoomsData.forEach((roomData, roomId) => {
      roomMap.set(roomId, {
        id: roomData.id,
        title: roomData.title || `Room ${roomData.id}`,
        clinicLocationName: getClinicLocationName(roomData),
        clinicBrandName: getClinicBrandName(roomData),
      })
    })
    
    // Then, update rooms that have slots in rosterData (to ensure they're included in the display)
    // But keep the clinic information from chamberRoomsData
    rosterData.forEach((slot) => {
      if (slot.chamberRoomId && validRoomIds.has(slot.chamberRoomId)) {
        const roomData = chamberRoomsData.get(slot.chamberRoomId)
        if (roomData) {
          // Update existing room entry, preserving clinic info from chamberRoomsData
          roomMap.set(slot.chamberRoomId, {
            id: roomData.id,
            title: roomData.title || slot.chamberRoomTitle || `Room ${slot.chamberRoomId}`,
            clinicLocationName: getClinicLocationName(roomData) || slot.clinicLocationName,
            clinicBrandName: getClinicBrandName(roomData) || slot.clinicBrandName,
          })
        }
      }
    })
    
    return Array.from(roomMap.values())
  }, [rosterData, unoccupiedSlots, chamberRoomsData])

  // Extract legend items (practitioners) from roster data
  // This is a fallback - actual mentors for form come from ChamberRoom.mentorsAndAuditors
  const legendItems = useMemo(() => {
    const practitionerMap = new Map()
    rosterData.forEach((slot) => {
      if (slot.mentorId && !practitionerMap.has(slot.mentorId)) {
        practitionerMap.set(slot.mentorId, {
          id: slot.mentorId,
          title: slot.mentorName || slot.mentorTitle || `Practitioner ${slot.mentorId}`,
          name: slot.mentorName || slot.mentorTitle,
          profilePicId: slot.mentorProfilePicId || null,
          specializations: slot.mentorSpecializations || null,
        })
      }
    })
    return Array.from(practitionerMap.values())
  }, [rosterData])
  
  // Create a map of mentorId -> mentor data (including profilePicId) for quick lookup
  const mentorDataMap = useMemo(() => {
    const map = new Map()
    
    // Add mentors from all rooms' associatedMentors
    chamberRoomsData.forEach((room) => {
      const mentors = room.associatedMentors || []
      mentors.forEach((mentor) => {
        if (mentor.id && !map.has(mentor.id)) {
          map.set(mentor.id, {
            id: mentor.id,
            name: mentor.name || mentor.title,
            title: mentor.name || mentor.title || `Mentor ${mentor.id}`,
            profilePicId: mentor.profilePicId || null,
          })
        }
      })
    })
    
    // Also add from legendItems (fallback from roster data)
    legendItems.forEach((item) => {
      if (item.id && !map.has(item.id)) {
        map.set(item.id, item)
      }
    })
    
    return map
  }, [chamberRoomsData, legendItems])

  // Get filtered mentors for a specific room from associatedMentors
  // Mentors are already filtered by RosterSchedule.eventTypes on the server
  // We just need to extract them from the room data
  const getFilteredMentorsForRoom = useMemo(() => {
    return (roomId, eventType) => {
      const room = chamberRoomsData.get(roomId)
      if (!room) {
        return []
      }
      
      // Get associatedMentors from room - already filtered by RosterSchedule.eventTypes on server
      const mentors = room.associatedMentors || []
      if (!Array.isArray(mentors) || mentors.length === 0) {
        return []
      }
      
      // Map to the format expected by RosterForm
      // associatedMentors structure: { id, name, slug, specializations, targetSpecialitySlugs, profilePicId }
      return mentors.map((mentor) => ({
        id: mentor.id,
        title: mentor.name || mentor.title || `Mentor ${mentor.id}`,
        name: mentor.name || mentor.title,
        profilePicId: mentor.profilePicId || null,
        specializations: mentor.specializations || null,
      }))
    }
  }, [chamberRoomsData])

  const handleSlotClick = (slot, event) => {
    // Use the clicked element as anchor for the popover
    const anchorEl = event?.currentTarget || event?.target || null
    popoverState.openViewPopover(slot, anchorEl)
  }

  const handleEmptySlotClick = (roomId, date, time, event, slotEventType = null) => {
    // Create a slot object for the form
    const slotData = {
      chamberRoomId: roomId,
      customDate: date,
      slotStartTime: time,
      slotEndTime: dayjs(`${date}T${time}`).add(30, 'minute').format('HH:mm'),
      eventType: slotEventType, // Include eventType if available from unoccupied slot
    }
    // Use the clicked element as anchor for the popover
    const anchorEl = event?.currentTarget || event?.target || null
    popoverState.openCreatePopover(slotData, anchorEl)
  }

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode)
    }
  }

  // Check if user has access
  if (!isClinicRosterAdmin && !isClinicRosterManager) {
    return (
      <OverallRosterContainer>
        <Alert severity="error" sx={{ m: 2 }}>
          You do not have permission to access this page.
        </Alert>
      </OverallRosterContainer>
    )
  }

  return (
    <OverallRosterContainer>
      <SupportHeader
        heading={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Overall Roster Management
            </Typography>
            {isClinicRosterManager && !isClinicRosterAdmin && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                (Limited Access)
              </Typography>
            )}
          </Box>
        }
      />

      {/* View Mode Toggle */}
      <ViewModeContainer className="no-print">
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="weekly" aria-label="weekly view">
            <ViewWeekIcon sx={{ mr: 1 }} />
            Weekly
          </ToggleButton>
          <ToggleButton value="daily" aria-label="daily view">
            <ViewDayIcon sx={{ mr: 1 }} />
            Daily
          </ToggleButton>
          <ToggleButton value="summary" aria-label="summary view">
            <AssessmentIcon sx={{ mr: 1 }} />
            Summary
          </ToggleButton>
        </ToggleButtonGroup>
      </ViewModeContainer>

      {/* Filters */}
      {viewMode !== 'summary' && (
        <Box className="no-print">
          <RosterFilters
            selectedRoomIds={selectedRoomIds}
            setSelectedRoomIds={setSelectedRoomIds}
            allowedClinicIds={allowedClinicIds}
            allowedClinics={allowedClinics}
            isRosterAdmin={isClinicRosterAdmin}
            selectedTargetSpecialitySlugs={selectedTargetSpecialitySlugs}
            setSelectedTargetSpecialitySlugs={setSelectedTargetSpecialitySlugs}
            selectedEventTypes={selectedEventTypes}
            setSelectedEventTypes={setSelectedEventTypes}
            onRoomsDataChange={handleRoomsDataChange}
            onTargetSpecialitiesChange={handleTargetSpecialitiesChange}
          />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {viewMode === 'weekly' && (
          <WeeklyView
            rosterData={rosterData}
            unoccupiedSlots={unoccupiedSlots}
            selectedWeek={selectedWeek}
            onWeekChange={setSelectedWeek}
            onSlotClick={handleSlotClick}
            onEmptySlotClick={handleEmptySlotClick}
            loading={loading}
            rooms={rooms}
            legendItems={legendItems}
            chamberRoomsData={chamberRoomsData}
            targetSpecialities={targetSpecialities}
            mentorDataMap={mentorDataMap}
          />
        )}

        {viewMode === 'daily' && (
          <DailyView
            rosterData={rosterData}
            unoccupiedSlots={unoccupiedSlots}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onSlotClick={handleSlotClick}
            onEmptySlotClick={handleEmptySlotClick}
            loading={loading}
            rooms={rooms}
            legendItems={legendItems}
            chamberRoomsData={chamberRoomsData}
            targetSpecialities={targetSpecialities}
            mentorDataMap={mentorDataMap}
          />
        )}

        {viewMode === 'summary' && (
          <SummaryView
            summary={summary}
            unoccupiedSlots={unoccupiedSlots}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={(from, to) => {
              // Handle date range change if needed
            }}
            onAssignSlot={handleEmptySlotClick}
            loading={loading}
          />
        )}
      </Box>

      {/* Popovers for slot creation/editing */}
      {popoverState.createPopoverAnchor && (() => {
        const selectedSlot = popoverState.selectedSlot
        const roomId = selectedSlot?.chamberRoomId
        const eventType = selectedSlot?.eventType // From unoccupied slot or user selection
        
        // Get mentors from associatedMentors - already filtered by RosterSchedule.eventTypes on server
        const filteredMentors = roomId ? getFilteredMentorsForRoom(roomId, eventType) : legendItems
        
        // Get roster schedule for the selected room
        const room = chamberRoomsData.get(roomId)
        const rosterSchedule = room?.rosterSchedule || null
        
        return (
          <RosterPopover
            open={!!popoverState.createPopoverAnchor}
            anchorEl={popoverState.createPopoverAnchor}
            onClose={popoverState.closeAllPopovers}
          >
            <RosterForm
              selectedSlot={selectedSlot}
              closePopup={popoverState.closeAllPopovers}
              legendItems={filteredMentors.length > 0 ? filteredMentors : legendItems}
              getSidebarLegendValue={(item) => item?.id}
              getSidebarLegendLabel={(item) => item?.title || item?.name || `Practitioner ${item?.id || 'Unknown'}`}
              getSidebarItemReferenceFromRosterItem={(item) => item?.mentorId}
              selectedItem={null}
              addEventToSlotList={() => {
                // Refresh roster data after slot creation
                refetch()
              }}
              updateSlotOnCalendar={() => {}}
              rosterSchedule={rosterSchedule}
            />
          </RosterPopover>
        )
      })()}

      {popoverState.viewPopoverAnchor && (
        <RosterPopover
          open={!!popoverState.viewPopoverAnchor}
          anchorEl={popoverState.viewPopoverAnchor}
          onClose={popoverState.closeAllPopovers}
        >
          <ClinicRosterItemViewer
            selectedSlot={(() => {
              // Enhance selected slot with mentor profilePicId if available
              const slot = popoverState.selectedSlot
              if (slot?.mentorId) {
                const mentorData = mentorDataMap.get(slot.mentorId)
                return {
                  ...slot,
                  mentorProfilePicId: slot.mentorProfilePicId || mentorData?.profilePicId || null,
                }
              }
              return slot
            })()}
            closePopup={popoverState.closeAllPopovers}
            legendItems={(() => {
              // Enhance legendItems with profilePicId from mentorDataMap
              return legendItems.map((item) => {
                const mentorData = mentorDataMap.get(item.id)
                return {
                  ...item,
                  profilePicId: item.profilePicId || mentorData?.profilePicId || null,
                }
              })
            })()}
            getSidebarLegendValue={(item) => item?.id}
            getSidebarLegendLabel={(item) => item?.title || item?.name || `Practitioner ${item?.id || 'Unknown'}`}
            getSidebarItemReferenceFromRosterItem={(item) => item?.mentorId}
            selectedItem={null}
            addEventToSlotList={() => {}}
            updateSlotOnCalendar={() => {}}
            removeSlotFromRosterList={() => {
              // Refresh roster data after slot removal
              refetch()
            }}
          />
        </RosterPopover>
      )}
    </OverallRosterContainer>
  )
}
